import React, { useEffect, useRef, useState } from 'react'
import { observer, inject } from 'mobx-react'

import { LINK_RULES } from '@client/utils/linkCheck'

import './styles.scss'

// LINK_RULES key -> i18n message id for the rule description shown on each row.
const RULE_MESSAGE_KEYS = {
  [LINK_RULES.EMPTY]: 'linkIssues.rule.empty',
  [LINK_RULES.PLACEHOLDER]: 'linkIssues.rule.placeholder',
  [LINK_RULES.OUTLOOK_SAFELINK]: 'linkIssues.rule.outlookSafelink',
  [LINK_RULES.WHITESPACE]: 'linkIssues.rule.whitespace',
  [LINK_RULES.BROKEN_MAILTO]: 'linkIssues.rule.brokenMailto',
  [LINK_RULES.MISSING_PROTOCOL]: 'linkIssues.rule.missingProtocol',
  [LINK_RULES.MALFORMED]: 'linkIssues.rule.malformed',
}

// Empty / placeholder mean "link not finished yet" (amber warning); everything
// else is a genuinely malformed link (red error).
const WARNING_RULES = new Set([LINK_RULES.EMPTY, LINK_RULES.PLACEHOLDER])

// How long a fixed row stays green before it starts animating out, and a
// fallback removal in case the CSS transitionend never fires (e.g. reduced
// motion / the panel being hidden mid-transition).
const FIXED_HOLD_MS = 2200
const LEAVE_FALLBACK_MS = 600

const escapeId = (id) =>
  typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id

// Content fingerprint of an issue. Used to reconcile the live issue list against
// the rows we're currently showing — as a *multiset*, so several identical
// placeholder links in one block stay as separate rows and fixing one doesn't
// disturb the others. It is NOT used as the React key (rows carry a stable id).
const issueKey = (issue) =>
  [
    issue.blockId,
    issue.anchorTextKey || '',
    issue.anchorText || '',
    issue.url || '',
    issue.ruleKey || '',
  ].join('')

export const LinkIssues = ({ app, editor }) => {
  const issues = editor.linkIssues
  const isOpen = editor.linkIssuesPanelOpen
  // Block currently open in the settings sidebar — used to keep its rows
  // highlighted so it's clear which link is being fixed.
  const activeBlockId = app.editingSettings && app.editingSettings.id
  // Block ids that still exist — lets us tell "link fixed" (block stays, issue
  // gone) from "block deleted" (no fixed flash for those).
  const blockIds = new Set(editor.getBlocks().map((b) => b.id))

  // Displayed rows = live issues, plus recently-fixed rows kept around briefly
  // in a green "fixed" state so the fix is acknowledged before they fade out.
  // Each entry has a stable `id` (its React key + timer handle), a `baseKey`
  // (content fingerprint, for multiset matching) and a `status`.
  const nextIdRef = useRef(0)
  const [display, setDisplay] = useState(() =>
    issues.map((issue) => ({
      id: nextIdRef.current++,
      baseKey: issueKey(issue),
      issue,
      status: 'active',
    }))
  )

  // Mirror of `display` for the reconcile effect (which must read the latest
  // committed list without re-running on every display change).
  const displayRef = useRef(display)
  useEffect(() => {
    displayRef.current = display
  }, [display])

  // Pending fixed -> leaving -> removed timers, keyed by entry id.
  const timersRef = useRef({})
  const clearTimer = (id) => {
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id])
      delete timersRef.current[id]
    }
  }
  useEffect(
    () => () => Object.keys(timersRef.current).forEach((id) => clearTimer(id)),
    []
  )

  const removeEntry = (id) => {
    clearTimer(id)
    setDisplay((prev) => prev.filter((e) => e.id !== id))
  }

  const startLeaving = (id) => {
    clearTimer(id)
    setDisplay((prev) =>
      prev.map((e) => (e.id === id ? { ...e, status: 'leaving' } : e))
    )
    timersRef.current[id] = setTimeout(() => removeEntry(id), LEAVE_FALLBACK_MS)
  }

  // Signature changes only when the *set* of issues changes (not on unrelated
  // edits elsewhere in the document), so we reconcile exactly when needed.
  const signature = issues.map(issueKey).join('')

  useEffect(() => {
    const prev = displayRef.current

    // Pool of reusable active rows, queued per content fingerprint so we match
    // current issues to existing rows one-for-one (multiset semantics).
    const pool = new Map()
    prev.forEach((e) => {
      if (e.status !== 'active') return
      if (!pool.has(e.baseKey)) pool.set(e.baseKey, [])
      pool.get(e.baseKey).push(e)
    })

    const updatedIssueByEntryId = new Map()
    const newIssues = []
    issues.forEach((issue) => {
      const queue = pool.get(issueKey(issue))
      if (queue && queue.length) {
        updatedIssueByEntryId.set(queue.shift().id, issue)
      } else {
        newIssues.push(issue)
      }
    })

    const next = []
    const newlyFixed = []
    prev.forEach((e) => {
      if (updatedIssueByEntryId.has(e.id)) {
        // Still an issue → keep it active in place (refresh the issue data).
        clearTimer(e.id)
        next.push({
          id: e.id,
          baseKey: e.baseKey,
          issue: updatedIssueByEntryId.get(e.id),
          status: 'active',
        })
      } else if (e.status !== 'active') {
        // Already animating out → leave it be until its timer removes it.
        next.push(e)
      } else if (blockIds.has(e.issue.blockId)) {
        // Disappeared while its block still exists → it was fixed.
        next.push({ ...e, status: 'fixed' })
        newlyFixed.push(e.id)
      }
      // else: the whole block was deleted → drop the row silently.
    })

    // Brand-new issues we weren't already showing, appended in order.
    newIssues.forEach((issue) => {
      next.push({
        id: nextIdRef.current++,
        baseKey: issueKey(issue),
        issue,
        status: 'active',
      })
    })

    setDisplay(next)
    newlyFixed.forEach((id) => {
      timersRef.current[id] = setTimeout(() => startLeaving(id), FIXED_HOLD_MS)
    })
    // Reconcile only when the issue set changes; the helpers are stable.
  }, [signature])

  const selectBlock = (blockId) => {
    const block = editor.getBlocks().find((b) => b.id === blockId)
    if (block) app.setEditingSettings(block)
    return block
  }

  const handleJump = (blockId) => {
    const el = document.querySelector(`[data-block-id="${escapeId(blockId)}"]`)
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    selectBlock(blockId)
  }

  return (
    <aside className={`link-issues-panel${isOpen ? ' is-open' : ''}`}>
      <div className="link-issues-panel--header">
        <div>
          <span className="link-issues-panel--title">
            {app.formatMessage('linkIssues.title')}
          </span>
          <span className="link-issues-panel--count">
            {app.formatMessage('linkIssues.count', { count: issues.length })}
          </span>
        </div>
        <button
          type="button"
          aria-label={app.formatMessage('linkIssues.close')}
          className="bp6-button bp6-minimal bp6-icon-cross"
          onClick={() => editor.setLinkIssuesPanelOpen(false)}
        />
      </div>

      <div className="link-issues-panel--body">
        <Choose>
          <When condition={!display.length}>
            <div className="link-issues-panel--empty">
              {app.formatMessage('linkIssues.empty')}
            </div>
          </When>
          <Otherwise>
            <For each="entry" of={display}>
              <button
                type="button"
                className={`link-issues-row${
                  entry.status === 'active' &&
                  entry.issue.blockId === activeBlockId
                    ? ' is-active'
                    : ''
                }${entry.status === 'fixed' ? ' is-fixed' : ''}${
                  entry.status === 'leaving' ? ' is-fixed is-leaving' : ''
                }`}
                key={entry.id}
                onClick={() => handleJump(entry.issue.blockId)}
                onTransitionEnd={
                  entry.status === 'leaving'
                    ? () => removeEntry(entry.id)
                    : undefined
                }
              >
                <span className="link-issues-row--main">
                  <span className="link-issues-row--type">
                    {app.formatMessage(entry.issue.labelKey)}
                  </span>
                  <span className="link-issues-row--title">
                    <Choose>
                      <When condition={entry.issue.anchorTextKey}>
                        {`“${app.formatMessage(entry.issue.anchorTextKey)}”`}
                      </When>
                      <When condition={entry.issue.anchorText}>
                        {`“${entry.issue.anchorText}”`}
                      </When>
                      <Otherwise>
                        <span className="link-issues-row--muted">
                          {app.formatMessage('linkIssues.noText')}
                        </span>
                      </Otherwise>
                    </Choose>
                  </span>
                  <span className="link-issues-row--sub">
                    <Choose>
                      <When condition={entry.status !== 'active'}>
                        <span className="link-issues-row--fixed">
                          {app.formatMessage('linkIssues.fixed')}
                        </span>
                      </When>
                      <Otherwise>
                        <span
                          className={`link-issues-row--rule${
                            WARNING_RULES.has(entry.issue.ruleKey)
                              ? ' is-warning'
                              : ''
                          }`}
                        >
                          {app.formatMessage(
                            RULE_MESSAGE_KEYS[entry.issue.ruleKey] ||
                              'linkIssues.rule.malformed'
                          )}
                        </span>
                        <If
                          condition={entry.issue.url && entry.issue.url.trim()}
                        >
                          <span
                            className="link-issues-row--url"
                            title={entry.issue.url}
                          >
                            {entry.issue.url}
                          </span>
                        </If>
                      </Otherwise>
                    </Choose>
                  </span>
                </span>
                <Choose>
                  <When condition={entry.status !== 'active'}>
                    <span className="link-issues-row--check bp6-icon bp6-icon-tick" />
                  </When>
                  <Otherwise>
                    <span className="link-issues-row--jump bp6-icon bp6-icon-arrow-right" />
                  </Otherwise>
                </Choose>
              </button>
            </For>
          </Otherwise>
        </Choose>
      </div>
    </aside>
  )
}

export default inject('app', 'editor')(observer(LinkIssues))
